import { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import type { Atom, Bond } from '@alchemiesolns/molecular-workspace';
import { AtomContainer, AtomModel, BondModel, AtomChirality, type IAtomModel } from '@alchemiesolns/atomcontainer';
import { VisibilityFlag } from '@alchemiesolns/visibility-config';
import { stableId } from '@alchemiesolns/stable-id';

/**
 * Transient ID-to-index maps built from Map iteration order.
 * Private to this module — never exposed. No ordering guarantees.
 */
export interface IndexMaps {
  atomIdToIndex: Map<string, number>;
  bondIdToIndex: Map<string, number>;
  atomIds: string[];
  bondIds: string[];
}

/**
 * Build transient index maps from a workspace's Maps.
 * Uses whatever iteration order the Maps provide (insertion order per JS spec,
 * but we make no guarantee about stability across serialization round-trips).
 */
export function buildIndexMaps(workspace: MolecularWorkspace): IndexMaps {
  const atomIdToIndex = new Map<string, number>();
  const bondIdToIndex = new Map<string, number>();
  const atomIds: string[] = [];
  const bondIds: string[] = [];

  let i = 0;
  for (const id of workspace.getAtomIds()) {
    atomIdToIndex.set(id, i++);
    atomIds.push(id);
  }

  i = 0;
  for (const id of workspace.getBondIds()) {
    bondIdToIndex.set(id, i++);
    bondIds.push(id);
  }

  return { atomIdToIndex, bondIdToIndex, atomIds, bondIds };
}

/**
 * Convert MolecularWorkspace to AtomContainer.
 *
 * Iterates workspace Maps directly — no ordering guarantee.
 * Sets atom IDs on the container for round-trip fidelity.
 */
export function toAtomContainer(workspace: MolecularWorkspace): AtomContainer {
  const container = new AtomContainer();
  const atomModelMap = new Map<string, IAtomModel>();

  for (const atom of workspace.getAllAtoms()) {
    const atomId = atom.id;
    const atomModel = new AtomModel(atom.symbol);
    atomModel.id = atomId;
    atomModel.charge = atom.charge;
    atomModel.lonePairs = atom.lonePairs;
    atomModel.radicals = atom.radicals;
    atomModel.pos = { x: atom.x, y: atom.y };
    // PIPH-269: round-trip R/S stereochemistry.
    if (atom.chirality === 'r') atomModel.chirality = AtomChirality.R;
    else if (atom.chirality === 's') atomModel.chirality = AtomChirality.S;

    const visibility = workspace.getVisibility(atomId);
    if (visibility === VisibilityFlag.Hidden) {
      atomModel.visible = false;
    }

    container.atoms.push(atomModel);
    atomModelMap.set(atomId, atomModel);
  }

  for (const bond of workspace.getAllBonds()) {
    const bondId = bond.id;
    const a1 = atomModelMap.get(bond.a1.id);
    const a2 = atomModelMap.get(bond.a2.id);

    if (!a1 || !a2) {
      throw new Error(`Bond ${bondId} references non-existent atoms`);
    }

    const bondModel = new BondModel(a1, a2, bond.order, bond.bondType);
    bondModel.id = bondId;
    // PIPH-269: round-trip E/Z stereochemistry.
    if (bond.isomerConfig) bondModel.isomerConfig = bond.isomerConfig;
    container.bonds.push(bondModel);
  }

  return container;
}

/**
 * Create MolecularWorkspace from AtomContainer.
 *
 * Preserves IDs from AtomContainer when available (MC-680 compat).
 * Generates new opaque IDs for atoms/bonds without IDs.
 */
export function fromAtomContainer(container: AtomContainer): MolecularWorkspace {
  const atoms = new Map<string, Atom>();
  const bonds = new Map<string, Bond>();
  const visibility = new Map<string, VisibilityFlag>();

  const atomIdMap = new Map<IAtomModel, string>();

  for (const atomModel of container.atoms) {
    const atomId = atomModel.id || `a${stableId()}`;

    const atom: Atom = {
      id: atomId,
      symbol: atomModel.symbol,
      x: atomModel.pos?.x ?? 0,
      y: atomModel.pos?.y ?? 0,
      charge: atomModel.charge,
      lonePairs: atomModel.lonePairs,
      radicals: atomModel.radicals,
      // PIPH-269: round-trip R/S stereochemistry.
      ...(atomModel.chirality === AtomChirality.R && { chirality: 'r' as const }),
      ...(atomModel.chirality === AtomChirality.S && { chirality: 's' as const }),
    };

    atoms.set(atomId, atom);
    atomIdMap.set(atomModel, atomId);

    if (atomModel.visible === false) {
      visibility.set(atomId, VisibilityFlag.Hidden);
    }
  }

  for (const bondModel of container.bonds) {
    const a1Id = atomIdMap.get(bondModel.a1);
    const a2Id = atomIdMap.get(bondModel.a2);

    if (!a1Id || !a2Id) {
      throw new Error('Bond references atom not in container');
    }

    const a1Atom = atoms.get(a1Id);
    const a2Atom = atoms.get(a2Id);

    if (!a1Atom || !a2Atom) {
      throw new Error('Bond references atom not found in atoms map');
    }

    const bondId = bondModel.id || `b${stableId()}`;
    const bondType = (bondModel.type as 'l' | 'd' | 'w' | 's' | undefined) ?? 'l';

    const bond: Bond = {
      id: bondId,
      a1: a1Atom,
      a2: a2Atom,
      order: bondModel.order,
      bondType,
      // PIPH-269: round-trip E/Z stereochemistry.
      ...(bondModel.isomerConfig && { isomerConfig: bondModel.isomerConfig }),
    };

    bonds.set(bondId, bond);
  }

  return new MolecularWorkspace(
    atoms,
    bonds,
    visibility,
    [],
    new Map(),
  );
}
